import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  TextField,
  Grid,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TableContainer,
  Pagination,
  Stack,
  Paper,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText
} from "@mui/material";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import ScaleLoader from "react-spinners/ScaleLoader";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import AssignmentIcon from '@mui/icons-material/Assignment';
import FlagIcon from '@mui/icons-material/Flag';
import API from "../../utils/Axios.utils";
import useStyles from "../../assets/styles/SupMangComp.style";
import noTableData from "../../assets/images/noTableData.svg";
import { Notification } from "../../utils/Notification";
import CustomModel from "../../utils/CustomModel";
import JobDetailsDialog from "../JobManagement/JobDetailsDialog";
import SearchIcon from '@mui/icons-material/Search';
import useDebounce from "../../utils/useDebounce";
import JobNotesDialog from "./JobNotesDialog";

import useJobManagement from "../../hooks/useJobManagement";
import useAuth from "../../hooks/useAuth";

// Style constants to prevent recreation on every render
const filterStackSx = { height: "100%", justifyContent: "center" };
const searchFieldSx = { width: 220 };
const paginationStackSx = {
  padding: 2,
  display: "flex",
  flexDirection: "row",
  justifyContent: "end",
};

const ListOfOpenJobs = ({ supplier }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [selectedCustomerId, setSelectedCustomerId] = useState(queryParams.get("customerId") || '');
  const [selectedCoreSkillId, setSelectedCoreSkillId] = useState(queryParams.get("coreSkills") || '');
  const [selectedStatus, setSelectedStatus] = useState(queryParams.get("status") || '');
  const [customers, setCustomers] = useState([]);
  const [coreSkillsData, setCoreSkillsData] = useState([]);

  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJobCode, setSelectedJobCode] = useState("");

  const { supplierId, token } = useAuth();

  const fetchUrlBase = supplier
    ? "/api/dash/openjoblist_supplier"
    : "/api/dash/openjoblist";

  const {
    page,
    listCount,
    handlePageChange,
    searchText,
    setSearchText,
    showLoader,
    jobData,
    alert,
    alertContent,
    alertType,
    handleAlertClose,
    forceUpdate,
  } = useJobManagement({
    fetchUrlBase,
    dataKey: supplier ? "job_post" : "job",
    extraParams: supplier
      ? { supplierId }
      : {
        customerId: selectedCustomerId,
        coreSkills: selectedCoreSkillId,
        status: selectedStatus
      },
    resetOnParamsChange: true,
  });

  const handleCustomerChange = useCallback((event) => {
    setSelectedCustomerId(event.target.value);
  }, []);

  const handleStatusChange = useCallback((event) => {
    setSelectedStatus(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    const newParams = new URLSearchParams(location.search);
    if (selectedCustomerId) newParams.set("customerId", selectedCustomerId);
    else newParams.delete("customerId");

    if (selectedCoreSkillId) newParams.set("coreSkills", selectedCoreSkillId);
    else newParams.delete("coreSkills");

    if (selectedStatus) newParams.set("status", selectedStatus);
    else newParams.delete("status");

    newParams.set("page", "1"); // Reset to page 1 on filter change
    navigate(`?${newParams.toString()}`, { replace: true });
    forceUpdate();
  }, [forceUpdate, navigate, location.search, selectedCustomerId, selectedCoreSkillId, selectedStatus]);

  const handleClearFilters = useCallback(() => {
    setSelectedCustomerId('');
    setSelectedCoreSkillId("");
    setSelectedStatus("");
    setSearchText("");
    const newParams = new URLSearchParams(location.search);
    newParams.delete("customerId");
    newParams.delete("coreSkills");
    newParams.delete("status");
    newParams.delete("search");
    newParams.set("page", "1");
    navigate(`?${newParams.toString()}`, { replace: true });
  }, [setSearchText, navigate, location.search]);

  const handleCoreSkillChange = useCallback((e) => {
    const id = e.target.value;
    setSelectedCoreSkillId(id);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
    const newParams = new URLSearchParams(location.search);
    if (e.target.value) newParams.set("search", e.target.value);
    else newParams.delete("search");
    newParams.set("page", "1");
    navigate(`?${newParams.toString()}`, { replace: true });
  }, [setSearchText, navigate, location.search]);

  const handleNotesClick = useCallback((jobId, jobCode) => {
    setSelectedJobId(jobId);
    setSelectedJobCode(jobCode);
    setOpenNotesDialog(true);
  }, []);

  const handleNotesDialogClose = useCallback(() => {
    setOpenNotesDialog(false);
  }, []);

  const fetchCoreSkills = async () => {
    try {
      // const token = JSON.parse(localStorage.getItem('userLoggedIn')).token;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await API.get('api/core-skill/coreSkills', config);
      setCoreSkillsData(res?.data?.coreSkills || []);
    } catch (error) {
      console.error('Error fetching core skills:', error);
      setCoreSkillsData([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      // const token = JSON.parse(localStorage.getItem('userLoggedIn')).token;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await API.get('/api/customer/active_customer_list', config);
      setCustomers(res?.data?.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  useEffect(() => {
    if (!supplier) { // Only for admin
      fetchCoreSkills(); // Fetch once on mount
      fetchCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseStatusLabel = useCallback((status) => {
    if (!status) return "";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);
  return (
    <div style={{ marginTop: "20px" }}>
      <Grid container xs={12} style={{ marginTop: "2%" }}>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <span className={classes.headingText}>List Of Open Jobs</span>
        </Grid>

        {!supplier && (
          <Grid container spacing={2} className={classes.filtersSection} style={{ marginTop: "1%" }}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="customer-label">Customer</InputLabel>
                <Select
                  labelId="customer-label"
                  id="customer"
                  label="Customer"
                  value={selectedCustomerId}
                  onChange={handleCustomerChange}
                >
                  {/* <MenuItem value="">
                    <em>All</em>
                  </MenuItem> */}
                  {customers.map((cust) => (
                    <MenuItem key={cust.customer_id} value={cust.customer_id}>
                      {cust.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Core Skills - simple select */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="coreskill-label"  >Select Core Skills</InputLabel>
                <Select
                  labelId="coreskill-label"
                  id="coreskill-label"
                  // displayEmpty
                  value={selectedCoreSkillId || ""}
                  label="Select Core Skills"
                  onChange={handleCoreSkillChange}
                >
                  {coreSkillsData.map((skill) => (
                    <MenuItem key={skill.id} value={skill.id}>
                      {skill.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status - simple select */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" >
                <InputLabel id="job-status">Job Status</InputLabel>
                <Select
                  value={selectedStatus || ""}
                  label="Job Status"
                  id="job-status"
                  onChange={handleStatusChange}
                // displayEmpty
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Partially-Fulfilled">Partially Fulfilled</MenuItem>
                  <MenuItem value="Full-Filled">Full Filled</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                  <MenuItem value="Hold">Hold</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="Close-No_Client_Response">Close-No Client Response</MenuItem>
                  <MenuItem value="Close-Unable_To_Fulfill">Close-Unable To Fulfill</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Apply / Clear */}
            <Grid item xs={12} md={2}>
              <Stack
                spacing={1}
                sx={filterStackSx}
              >
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="small"
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearFilters}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
            </Grid>

            {/* Search - reduced width */}
            <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <TextField
                size="small"
                placeholder="Search by Job Code or Title"
                value={searchText}
                onChange={handleSearchChange}
                sx={searchFieldSx}
              />
            </Grid>
          </Grid>
        )}





      </Grid>
      <Grid container xs={12} className={classes.tableGrid}>
        {showLoader ? (
          <div
            style={{
              display: "flex",
              height: 400,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <ScaleLoader size={250} color={"#0576DC"} loading={showLoader} />
          </div>
        ) : jobData?.length === 0 ? (
          <div
            style={{
              height: 300,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <img src={noTableData} width={60} alt="No Data" />
            <div
              style={{
                marginTop: 5,
                fontWeight: "500",
                fontSize: 25,
                fontFamily: "'Noto Sans', sans-serif",
              }}
            >
              There are no recent Jobs to show.
            </div>
          </div>
        ) : (
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: 650 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell className={classes.tableHeadCell}>
                    Job Code
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Job Title
                  </TableCell>
                  {!supplier && (
                    <TableCell className={classes.tableHeadCell}>
                      Customer
                    </TableCell>
                  )}
                  <TableCell className={classes.tableHeadCell}>
                    Core Skills
                  </TableCell>
                  {!supplier && (
                    <TableCell className={classes.tableHeadCell}>
                      Created Date
                    </TableCell>
                  )}
                  {!supplier && (
                    <TableCell className={classes.tableHeadCell}>
                      Job Age (Days)
                    </TableCell>
                  )}
                  {!supplier && (
                    <TableCell className={classes.tableHeadCell}>
                      Required Positions
                    </TableCell>
                  )}
                  {!supplier && (
                    <TableCell className={classes.tableHeadCell}>
                      Full-Filled Positions
                    </TableCell>
                  )}
                  {!supplier && (
                    <TableCell className={classes.tableHeadCell}>
                      Candidates Supplied
                    </TableCell>
                  )}
                  <TableCell className={classes.tableHeadCell}>
                    Status
                  </TableCell>
                  {/* {supplier && (                    
                  )} */}
                  {!supplier && (
                    <TableCell className={classes.tableHeadCell}>
                      Notes
                    </TableCell>
                  )}
                  <TableCell className={classes.tableHeadCell}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <>
                <TableBody className={classes.noHorizontalBorderTableRow}>
                  {jobData.map((val) => {
                    return (
                      <TableRow>
                        <TableCell className={classes.tableBodyCell}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {!supplier && val.has_no_activity ? (
                              <Tooltip title="No activity for more than 2 days">
                                <FlagIcon sx={{ color: "red", fontSize: "1.2rem" }} />
                              </Tooltip>
                            ) : null}
                            <span>{val.job_code}</span>
                          </Stack>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>
                          {val.title}
                        </TableCell>
                        {!supplier && (
                          <TableCell className={classes.tableBodyCell}>
                            {val?.customer?.name}
                          </TableCell>
                        )}
                        <TableCell className={classes.tableBodyCell}>
                          {val?.jobCoreSkill?.name || ""}
                        </TableCell>
                        {!supplier && (
                          <TableCell className={classes.tableBodyCell}>
                            {val.createdAt ? format(new Date(val.createdAt), "dd-MM-yyyy") : '-'}
                          </TableCell>
                        )}
                        {!supplier && (
                          <TableCell className={classes.tableBodyCell}>
                            {val?.agingInDays || 0}
                          </TableCell>
                        )}
                        {!supplier && (
                          <TableCell className={classes.tableBodyCell}>
                            {val?.no_of_requirements || 0}
                          </TableCell>
                        )}
                        {!supplier && (
                          <TableCell className={classes.tableBodyCell}>
                            {val?.fulfilled_count || 0}
                          </TableCell>
                        )}
                        {!supplier && (
                          <TableCell className={classes.tableBodyCell}>
                            {val?.candidate_count || 0}
                          </TableCell>
                        )}
                        <TableCell className={classes.tableBodyCell}>
                          {parseStatusLabel(val.job_status) || '-'}
                        </TableCell>
                        {/* {supplier && (
                          
                        )} */}
                        {!supplier && (
                          <TableCell className={classes.tableBodyCell}>
                            <IconButton
                              onClick={() => handleNotesClick(val.job_id, val.job_code)}
                            >
                              📝
                            </IconButton>
                          </TableCell>
                        )}
                        <TableCell>
                          <MoreMenu
                            navigate={navigate}
                            details={val}
                            supplier={supplier}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </>
            </Table>
            <Stack
              spacing={2}
              sx={paginationStackSx}
            >
              <Pagination
                classes={{
                  root: classes.pagination,
                }}
                count={Math.ceil(listCount / 10)}
                page={page}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
              />
            </Stack>
          </TableContainer>
        )}
      </Grid>
      <Notification
        message={alertContent}
        open={alert}
        severity={alertType}
        onClose={handleAlertClose}
      />
      <JobNotesDialog
        open={openNotesDialog}
        onClose={handleNotesDialogClose}
        jobId={selectedJobId}
        jobCode={selectedJobCode}
      />
    </div >
  );
};

export default ListOfOpenJobs;

const MoreMenu = React.memo(function MoreMenu({ details, navigate, supplier }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const handleDialog = useCallback(() => setOpen(!open), [open]);

  const handleAddCandidate = useCallback(() => {
    setOpen(false); // optional: close the modal before navigation
    navigate("/candidate_data/addCandidate", {
      state: { jobCode: details.job_code, from: location.search },
    });
  }, [navigate, details.job_code, location.search]);

  const { token } = useAuth();
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const handleViewClick = useCallback(() => {
    if (supplier) {
      // fetchCandidates(details.job_code);
    }
  }, [supplier]);

  const handleCandidateDataClick = useCallback(() => {
    navigate("/candidate_data", {
      state: { jobCode: details.job_code, from: location.search },
    });
  }, [navigate, details.job_code, location.search]);

  const handleAddCandidateClick = useCallback(() => {
    navigate("/candidate_data/addCandidate", {
      state: { jobCode: details.job_code, from: location.search },
    });
  }, [navigate, details.job_code, location.search]);

  const handleAdminAccordionClick = useCallback(() => {
    navigate("/admindashboard/adminAccordion", {
      state: { jobCode: details.job_code, from: location.search },
    });
  }, [navigate, details.job_code, location.search]);

  return (
    <Stack direction="row" sx={{ whiteSpace: "nowrap" }}>
      {supplier ? (
        <>
          <IconButton
            onClick={handleCandidateDataClick}
          >
            <RemoveRedEyeIcon sx={{ color: "#2777AA" }} />
          </IconButton>
          <IconButton
            onClick={handleDialog}
          >
            <AssignmentIcon sx={{ color: "#2777AA" }} />
          </IconButton>
          <IconButton
            onClick={handleAddCandidateClick}
          >
            <EditIcon sx={{ color: "#2777AA" }} />
          </IconButton>
          <CustomModel
            handleDialog={handleDialog}
            open={open}
            title="Job Description"
            onAddCandidate={handleAddCandidate}
          >
            <JobDetailsDialog details={details} />
          </CustomModel>
        </>

      ) : (
        <>
          <IconButton
            onClick={handleAdminAccordionClick}
          >
            <RemoveRedEyeIcon sx={{ color: "#2777AA" }} />
          </IconButton>
          <IconButton
            onClick={handleDialog}
          >
            <AssignmentIcon sx={{ color: "#2777AA" }} />
          </IconButton>
          <CustomModel
            handleDialog={handleDialog}
            open={open}
            title="Job Description"
          >
            <JobDetailsDialog details={details} />
          </CustomModel>
        </>
      )}
    </Stack>
  );
});
